import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Truck, Loader2 } from 'lucide-react';
import { formatPrice } from '../lib/utils';
import { trackPurchase } from '../lib/tracker';

const API_URL = '';

export default function OrderSuccessPage() {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentChecking, setPaymentChecking] = useState(false);

  const orderId = searchParams.get('order_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/orders/${orderId}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setOrder(data);
          // Meta Pixel backup fire (covers refresh / email-link landings).
          // Dedup via sessionStorage; same eventID as CodOrderForm/CheckoutPage,
          // so Meta silently drops duplicates if multiple sources fire.
          const fireKey = `meta_purchase_fired_${data.order_id}`;
          if (!sessionStorage.getItem(fireKey)) {
            trackPurchase(
              data.order_id,
              data.total,
              data.wilaya,
              data.delivery_type,
              (data.items || []).map(it => ({
                id: it.product_id,
                quantity: it.quantity,
                item_price: it.price,
              }))
            );
            sessionStorage.setItem(fireKey, '1');
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    const checkPaymentStatus = async () => {
      if (!sessionId) return;

      setPaymentChecking(true);
      let attempts = 0;
      const maxAttempts = 5;

      const poll = async () => {
        try {
          const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
          if (response.ok) {
            const data = await response.json();
            
            if (data.payment_status === 'paid') {
              setPaymentChecking(false);
              fetchOrder();
              return;
            }
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            setPaymentChecking(false);
            fetchOrder();
          }
        } catch (error) {
          console.error('Error checking payment:', error);
          setPaymentChecking(false);
          fetchOrder();
        }
      };

      poll();
    };

    if (sessionId) {
      checkPaymentStatus();
    } else {
      fetchOrder();
    }
  }, [orderId, sessionId]);

  if (loading || paymentChecking) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-[#D4AF37] mx-auto mb-4" />
          <p className="text-[#6B6A68]">
            {paymentChecking ? 'Vérification du paiement...' : 'Chargement...'}
          </p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif text-2xl mb-4">Commande non trouvée</p>
          <Link to="/shop" className="btn-primary px-6 py-3 inline-block">
            Retour à la boutique
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="order-success-page">
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 mx-auto mb-8 bg-[#A3B19B] rounded-full flex items-center justify-center">
          <CheckCircle size={40} className="text-white" />
        </div>

        <h1 className="font-serif text-3xl mb-4">Merci pour ta commande!</h1>
        <p className="text-[#6B6A68] mb-8">
          Ta commande <span className="font-medium text-[#1A1514]">#{order.order_id}</span> a été confirmée.
          {order.payment_method === 'cod' ? (
            ' Tu recevras un appel pour confirmer la livraison.'
          ) : (
            ' Un email de confirmation t\'a été envoyé.'
          )}
        </p>

        {/* Order Status */}
        <div className="bg-white p-6 mb-8 text-left">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-[#F4E3E1] rounded-full flex items-center justify-center">
              <Package size={20} className="text-[#D4AF37]" />
            </div>
            <div>
              <p className="font-medium">Statut de la commande</p>
              <p className="text-sm text-[#D4AF37] capitalize">{order.order_status}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#F4E3E1] rounded-full flex items-center justify-center">
              <Truck size={20} className="text-[#D4AF37]" />
            </div>
            <div>
              <p className="font-medium">Livraison</p>
              <p className="text-sm text-[#6B6A68]">{order.wilaya} - {order.shipping_address}</p>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white p-6 text-left">
          <h2 className="font-serif text-xl mb-4">Récapitulatif</h2>
          
          <div className="space-y-4 mb-6">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-[#6B6A68]">
                  {item.name} ({item.size}) × {item.quantity}
                </span>
                <span>{formatPrice(item.price * item.quantity)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-[#E0DFDD] pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#6B6A68]">Sous-total</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B6A68]">Livraison</span>
              <span>{order.shipping_cost === 0 ? 'Gratuit' : formatPrice(order.shipping_cost)}</span>
            </div>
            <div className="flex justify-between font-medium text-lg pt-2 border-t border-[#E0DFDD]">
              <span>Total</span>
              <span className="font-serif">{formatPrice(order.total)}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-[#E0DFDD]">
            <p className="text-sm text-[#6B6A68]">
              Mode de paiement: {order.payment_method === 'cod' ? 'Paiement à la livraison' : 'Carte bancaire'}
              {order.payment_method !== 'cod' && (
                <span className={order.payment_status === 'paid' ? ' text-[#A3B19B]' : ' text-[#D4AF37]'}>
                  {' '}({order.payment_status === 'paid' ? 'Payé' : 'En attente'})
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
          <Link 
            to="/shop" 
            className="btn-primary px-8 py-4 text-sm font-medium uppercase tracking-wider"
            data-testid="continue-shopping-btn"
          >
            Continuer mes achats
          </Link>
          <Link 
            to="/orders" 
            className="btn-secondary px-8 py-4 text-sm font-medium uppercase tracking-wider"
            data-testid="view-orders-btn"
          >
            Voir mes commandes
          </Link>
        </div>
      </div>
    </div>
  );
}
